import React from 'react';
import ErrorFallback from './ErrorFallback';
import logo from '../../assets/GreatChildLogo-transparent.webp';

export const APP_ERROR_EVENT = 'app-error' as const;

export function reportAppError(error: Error, meta?: Record<string, unknown>) {
  window.dispatchEvent(new CustomEvent(APP_ERROR_EVENT, { detail: { error, meta } }));
}

type Props = { children: React.ReactNode };
type State = { hasError: boolean; error: Error | null };

export class ErrorBoundary extends React.Component<Props, State> {
  public state: State = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // TODO: send to your logger
    // console.error('Boundary caught:', error, info);
  }

  private onWindowError = (event: ErrorEvent) => {
    const err = (event?.error as Error) || new Error(event?.message || 'Runtime error');
    if (!this.state.hasError) this.setState({ hasError: true, error: err });
  };

  private onUnhandledRejection = (event: PromiseRejectionEvent) => {
    const reason = event?.reason;
    const err = reason instanceof Error ? reason : new Error(String(reason ?? 'Unhandled rejection'));
    if (!this.state.hasError) this.setState({ hasError: true, error: err });
  };

  private onAppError = (event: Event) => {
    const detail = (event as CustomEvent<{ error?: Error }>).detail;
    const err = detail?.error ?? new Error('Application error');
    if (!this.state.hasError) this.setState({ hasError: true, error: err });
  };

  componentDidMount(): void {
    window.addEventListener('error', this.onWindowError);
    window.addEventListener('unhandledrejection', this.onUnhandledRejection);
    window.addEventListener(APP_ERROR_EVENT, this.onAppError as EventListener);
  }

  componentWillUnmount(): void {
    window.removeEventListener('error', this.onWindowError);
    window.removeEventListener('unhandledrejection', this.onUnhandledRejection);
    window.removeEventListener(APP_ERROR_EVENT, this.onAppError as EventListener);
  }

  render(): React.ReactNode {
    if (this.state.hasError) {
      return (
        <ErrorFallback
          redirectSeconds={5}
          showDetails={false}
          error={this.state.error}
          logoSrc={logo}
        />
      );
    }
    return this.props.children;
  }
}
